import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
  Tooltip,
  IconButton,
  Snackbar,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import { saveTestSession, getActiveSession } from '../services/testSessionService';
import { useActiveTimer } from '../hooks/useActiveTimer';
import CustomAppBar from '../components/CustomAppBar';
import ReactMarkdown from 'react-markdown';

// ─── Filter Option Definitions ─────────────────────────────────────────────
const FILTER_OPTIONS = [
    {
        value: 'all',
        label: 'All Questions',
        description: 'Include every question in the subject',
        color: 'primary',
    },
    {
        value: 'not_attempted',
        label: 'Not Attempted Yet',
        description: 'Only questions you have never answered before',
        color: 'info',
    },
    {
        value: 'wrong_last',
        label: 'Wrong in Last Attempt',
        description: 'Questions where your most recent marked attempt was incorrect',
        color: 'warning',
    },
    {
        value: 'wrong_recent_3',
        label: 'Wrong in Any of Last 3',
        description: 'Questions with at least one incorrect answer in the last 3 recorded attempts',
        color: 'error',
    },
];

// ─── Extracted per-question component (avoids full-page re-render on keystroke) ─
const QuestionItemComponent = ({ q, index, initialAnswer, onChange }) => {
    const [localText, setLocalText] = useState(initialAnswer || '');

    const handleChange = (e) => setLocalText(e.target.value);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (localText !== initialAnswer) onChange(q.id, localText);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [localText, q.id, initialAnswer, onChange]);

    return (
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Question {index + 1}</Typography>
                <Chip
                    label={`ID: ${q.id}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                />
            </Box>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
                <ReactMarkdown>{q.questionText}</ReactMarkdown>
            </Box>
            <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Type your answer here..."
                variant="outlined"
                value={localText}
                onChange={handleChange}
            />
        </Paper>
    );
};
const QuestionItem = React.memo(QuestionItemComponent);

// ─── Main Page ───────────────────────────────────────────────────────────────
const PracticeTestPage = () => {
    const { subjectId } = useParams();
    const navigate = useNavigate();

    // questions are now sourced from the session snapshot (filtered), NOT from the all-questions API
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copySnackbar, setCopySnackbar] = useState(false);

    // Filter dialog state
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [filterStarting, setFilterStarting] = useState(false); // loading spinner in dialog
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [filterLabel, setFilterLabel] = useState('All Questions');
    const [testStarted, setTestStarted] = useState(false);

    // Session state
    const [answers, setAnswers] = useState({});
    const [sessionId, setSessionId] = useState(null);

    const storedData = JSON.parse(localStorage.getItem(`TEST_SESSION_${subjectId}`) || '{}');
    const { timeInSeconds } = useActiveTimer(storedData.timeInSeconds || 0);

    // ── On mount: check for an active session or show the filter dialog ──────
    useEffect(() => {
        window.scrollTo(0, 0);

        const init = async () => {
            try {
                const activeSession = await getActiveSession(subjectId);

                if (activeSession) {
                    // Resumed session — use the snapshot's filtered questions
                    setSessionId(activeSession.id);
                    setQuestions(activeSession.questions || []);
                    // Merge: localStorage answers take precedence (more recent local edits)
                    const localAnswers = storedData.answers || {};
                    const backendAnswers = activeSession.answers || {};
                    setAnswers({ ...backendAnswers, ...localAnswers });
                    setFilterLabel('Resumed Session');
                    setTestStarted(true);
                } else {
                    // New session — show the filter selection dialog
                    setFilterDialogOpen(true);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectId]);

    // ── Filter confirmed: create session and use its filtered question list ───
    const handleFilterConfirm = async () => {
        setFilterStarting(true);
        try {
            const response = await saveTestSession({
                subjectId: parseInt(subjectId),
                activeTimeSeconds: 0,
                isCompleted: false,
                filterMode: selectedFilter,
                answers: {},
            });

            // response is ApiResponse<TestSessionDto> → actual DTO is in response.data
            const session = response?.data;
            if (session) {
                setSessionId(session.id);
                setQuestions(session.questions || []);
                localStorage.setItem(`TEST_SESSION_${subjectId}`, JSON.stringify({
                    sessionId: session.id,
                    timeInSeconds: 0,
                    answers: {},
                }));
            }

            const opt = FILTER_OPTIONS.find(f => f.value === selectedFilter);
            setFilterLabel(opt?.label || 'All Questions');
            setFilterDialogOpen(false);
            setTestStarted(true);
        } catch (err) {
            alert('Failed to start test: ' + err.message);
        } finally {
            setFilterStarting(false);
        }
    };

    const handleAnswerChange = useCallback((questionId, text) => {
        setAnswers(prev => ({ ...prev, [questionId]: text }));
    }, []);

    const handleSaveProgress = async () => {
        setSaving(true);
        try {
            const response = await saveTestSession({
                id: sessionId,
                subjectId: parseInt(subjectId),
                activeTimeSeconds: timeInSeconds,
                isCompleted: false,
                answers,
            });

            const session = response?.data;
            if (session?.id) {
                setSessionId(session.id);
                localStorage.setItem(`TEST_SESSION_${subjectId}`, JSON.stringify({
                    sessionId: session.id,
                    timeInSeconds,
                    answers,
                }));
            }
            alert('Progress saved successfully!');
        } catch (err) {
            alert('Failed to save progress: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleFinishTest = async () => {
        if (!window.confirm("Are you sure you want to finish the test?")) return;

        setSaving(true);
        try {
            await saveTestSession({
                id: sessionId,
                subjectId: parseInt(subjectId),
                activeTimeSeconds: timeInSeconds,
                isCompleted: true,
                answers,
            });

            localStorage.removeItem(`TEST_SESSION_${subjectId}`);
            setIsCompleted(true);
            window.scrollTo(0, 0);
        } catch (err) {
            alert('Failed to finish test: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h > 0 ? h + 'h ' : ''}${m}m ${s}s`;
    };

    const buildResultsText = () =>
        questions.map((q, i) =>
            `--- Question ${i + 1} [ID: ${q.id}] ---\n` +
            `Q: ${q.questionText}\n` +
            `Your Answer: ${answers[q.id] || 'No answer provided'}\n` +
            `Correct Answer: ${q.answerText || 'No correct answer provided'}\n`
        ).join('\n');

    const handleCopyResults = () => {
        navigator.clipboard.writeText(buildResultsText()).then(() => setCopySnackbar(true));
    };

    // ── Loading / Error ──────────────────────────────────────────────────────
    if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;

    // ── Completed / Results Screen ───────────────────────────────────────────
    if (isCompleted) {
        return (
            <CustomAppBar>
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Paper elevation={3} sx={{ p: 4 }}>
                        <Typography variant="h4" gutterBottom>Practice Test Results</Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                            Active Test Duration: {formatTime(timeInSeconds)}
                        </Typography>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            Copy the block below and paste it into an AI tool for evaluation.
                            Ask the AI to respond with:
                            <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                {`{ "results": [ { "questionId": <id>, "correct": true|false } ] }`}
                            </Box>
                        </Alert>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <Tooltip title="Copy results to clipboard">
                                <IconButton onClick={handleCopyResults} color="primary" size="small">
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box sx={{
                            bgcolor: '#1e1e1e',
                            color: '#d4d4d4',
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid #333',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.82rem',
                            maxHeight: '60vh',
                            overflowY: 'auto',
                        }}>
                            {questions.map((q, i) => (
                                <Box key={q.id} sx={{ mb: 3 }}>
                                    {`--- Question ${i + 1} [ID: ${q.id}] ---\n`}
                                    {`Q: ${q.questionText}\n`}
                                    {`Your Answer: ${answers[q.id] || 'No answer provided'}\n`}
                                    {`Correct Answer: ${q.answerText || 'No correct answer provided'}\n`}
                                </Box>
                            ))}
                        </Box>

                        <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ mt: 3 }}>
                            Back to Dashboard
                        </Button>
                    </Paper>
                </Container>

                <Snackbar
                    open={copySnackbar}
                    autoHideDuration={2500}
                    onClose={() => setCopySnackbar(false)}
                    message="Results copied to clipboard!"
                />
            </CustomAppBar>
        );
    }

    // ── Active Test Screen ───────────────────────────────────────────────────
    return (
        <CustomAppBar>
            {/* Filter Selection Dialog — shown for new sessions only */}
            <Dialog
                open={filterDialogOpen}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
                onClose={() => {}}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                        Select Questions to Practice
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Choose which questions to include based on your past attempt history.
                        This selection cannot be changed once the test begins.
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Filter Mode
                        </FormLabel>
                        <RadioGroup
                            value={selectedFilter}
                            onChange={(e) => setSelectedFilter(e.target.value)}
                        >
                            {FILTER_OPTIONS.map((opt) => (
                                <Paper
                                    key={opt.value}
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        mb: 1.5,
                                        borderRadius: 2,
                                        borderColor: selectedFilter === opt.value ? 'primary.main' : 'divider',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                    }}
                                    onClick={() => setSelectedFilter(opt.value)}
                                >
                                    <FormControlLabel
                                        value={opt.value}
                                        control={<Radio color={opt.color} />}
                                        label={
                                            <Box>
                                                <Typography variant="body1" fontWeight={600}>{opt.label}</Typography>
                                                <Typography variant="body2" color="text.secondary">{opt.description}</Typography>
                                            </Box>
                                        }
                                        sx={{ m: 0, alignItems: 'flex-start' }}
                                    />
                                </Paper>
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleFilterConfirm}
                        disabled={filterStarting}
                        startIcon={filterStarting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {filterStarting ? 'Starting…' : `Start Test with "${FILTER_OPTIONS.find(f => f.value === selectedFilter)?.label}"`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sticky Timer Bar — shown once test is active */}
            {testStarted && (
                <>
                    <Box sx={{
                        position: 'sticky',
                        top: 0,
                        bgcolor: 'background.paper',
                        zIndex: 10,
                        borderBottom: '1px solid #e0e0e0',
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6" color="primary">
                                Active Time: {formatTime(timeInSeconds)}
                            </Typography>
                            <Chip
                                icon={<FilterListIcon />}
                                label={filterLabel}
                                size="small"
                                variant="outlined"
                                color="secondary"
                            />
                            <Chip
                                label={`${questions.length} question${questions.length !== 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                        <Box>
                            <Button
                                variant="outlined"
                                onClick={handleSaveProgress}
                                disabled={saving}
                                sx={{ mr: 2 }}
                            >
                                {saving ? <CircularProgress size={24} /> : 'Save Progress'}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleFinishTest}
                                disabled={saving}
                            >
                                Finish
                            </Button>
                        </Box>
                    </Box>

                    <Container maxWidth="md" sx={{ py: 4 }}>
                        <Typography variant="h4" gutterBottom>Practice Test</Typography>
                        <Typography variant="body1" color="text.secondary" mb={4}>
                            The active timer pauses automatically if you switch tabs. Your progress is saved locally.
                        </Typography>

                        {questions.length === 0 ? (
                            <Alert severity="info">
                                No questions matched the selected filter. Try a different filter mode.
                            </Alert>
                        ) : (
                            questions.map((q, i) => (
                                <QuestionItem
                                    key={q.id}
                                    q={q}
                                    index={i}
                                    initialAnswer={answers[q.id] || ''}
                                    onChange={handleAnswerChange}
                                />
                            ))
                        )}
                    </Container>
                </>
            )}
        </CustomAppBar>
    );
};

export default PracticeTestPage;
